import React, { useCallback, useEffect, useState } from 'react';
import { ObjectSchema } from 'yup';
import { prepareDataForValidation } from 'formik';
import { focusOnField } from 'helpers/hooks/useScrollToErrorField';

interface Props {
  validationSchema: ObjectSchema;
  onlyTouched?: boolean;
  onlyRequired?: boolean;
  enabled?: boolean;
  values: any;
}

export interface Context {
  errors: any;
  onlyTouched: boolean;
  onlyRequired: boolean;
}

const defaultValues = {
  errors: {},
  onlyTouched: false,
  onlyRequired: false,
  enabled: false,
};

export const customErrorMessageClassName = 'custom-error-message';
export const CustomValidationContext = React.createContext<Context>(defaultValues);

const focusOnFirstFieldWithError = () => {
  const element: HTMLElement | null = document.querySelector(`.${customErrorMessageClassName}`);
  if (!element) {
    return;
  }
  const fieldName = element.dataset.fieldName || null;
  if (!fieldName) {
    return;
  }
  focusOnField(fieldName);
};

const CustomValidation: React.FC<Props> = ({
  children,
  values,
  validationSchema,
  onlyTouched,
  onlyRequired,
  enabled,
}) => {
  const [scrolled, setScrolled] = useState<boolean>(false);
  const [context, setContext] = useState<Context>({
    ...defaultValues,
    onlyRequired: onlyRequired !== undefined ? onlyRequired : defaultValues.onlyRequired,
    onlyTouched: onlyTouched !== undefined ? onlyTouched : defaultValues.onlyTouched,
  });

  const handleValuesUpdated = useCallback(async () => {
    const preparedData = prepareDataForValidation(values);
    try {
      await validationSchema.validate(preparedData, { abortEarly: false, stripUnknown: false });
      setContext({ ...context, errors: {} });
    } catch (e) {
      const filter = context.onlyRequired ? e.inner.filter((e: any) => e.type === 'required') : e.inner;
      const errors = filter.reduce((prev: any, curr: any) => {
        return {
          ...prev,
          [curr.path.replace(/\[(\d+)]/g, '.$1')]: curr.message,
        };
      }, {});
      setContext({ ...context, errors });
      if (!scrolled && Object.keys(errors).length > 0) {
        setScrolled(true);
        focusOnFirstFieldWithError();
      }
    }
  }, [values, validationSchema, context, scrolled]);

  useEffect(() => {
    if (enabled) {
      handleValuesUpdated();
    } else {
      setContext({ ...context, errors: {} });
    }
  }, [enabled, handleValuesUpdated, context]);

  return <CustomValidationContext.Provider value={context}>{children}</CustomValidationContext.Provider>;
};

export default CustomValidation;
